import { ShippingConfiguration, toReadableReturnReason } from '@/hooks'
import { ShippingRule } from '@/application/manage-shipping'

type TranslateFn = (key: string, options?: { lng?: string }) => string

// Return reasons are the one value family the agent emits in wire form (keys
// like WRONG_SIZE_TOO_SMALL) while the screen — and the save mapper — speak
// display labels. Supplying this lets normalization close that gap.
export interface ReasonCrosswalk {
  keys: string[]
  trans: (key: string) => string
}

// The chat agent may return a condition's function/comparison as a human
// display label ("Country", "=") instead of the wire key the rule cards match
// on ("country", "Equal"). Map each back to a valid key so the UI resolves.
// Values go the other way: the screen holds display names, so a wire-form
// reason key is translated to its label — left raw it would save as "".
export function normalizeChatRules(
  rules: ShippingRule[],
  availableConfiguration: ShippingConfiguration,
  t: TranslateFn,
  reasons?: ReasonCrosswalk
): ShippingRule[] {
  const functionKeys = availableConfiguration.functions.map((f) => f.function)

  return rules.map((rule) => ({
    ...rule,
    conditions: rule.conditions.map((condition) => {
      const func = resolveKey(condition.function, functionKeys, (key) => ({
        current: t('edit_shipping.function_names.' + key),
        english: t('edit_shipping.function_names.' + key, { lng: 'en' })
      }))
      const comparisonKeys =
        availableConfiguration.functions.find((f) => f.function === func)
          ?.comparison ?? []
      const comparison = resolveKey(
        condition.comparison,
        comparisonKeys,
        (key) => ({
          current: t('edit_shipping.comparisons.' + key),
          english: t('edit_shipping.comparisons.' + key, { lng: 'en' })
        })
      )
      return {
        ...condition,
        function: func,
        comparison,
        values: resolveValues(func, condition.values, reasons)
      }
    })
  }))
}

// Only returnReason needs it today: every other family is already emitted as a
// display name. An unrecognized value is left untouched so the user still sees
// what the agent proposed rather than a silent substitution.
function resolveValues(
  func: string,
  values: string[],
  reasons?: ReasonCrosswalk
): string[] {
  if (func !== 'returnReason' || !reasons) return values
  return values.map((value) => {
    if (!reasons.keys.includes(value)) return value
    // trans() yields '' for a reason the merchant has no copy for in the active
    // language. Keeping the key beats an empty label, which would save as an
    // empty condition — the very bug this normalization exists to prevent.
    return toReadableReturnReason(value, reasons.trans) || value
  })
}

const normalize = (value: string): string =>
  value.toLowerCase().replace(/[^a-z0-9]/g, '')

// Resolve a possibly-labelled value to one of the valid keys. Exact matches run
// first so symbol labels ("=", ">") that normalize to empty still resolve;
// normalized matches then cover case/spacing drift ("Not In" -> "NotIn").
function resolveKey(
  value: string,
  keys: string[],
  labelsFor: (key: string) => { current: string; english: string }
): string {
  if (!value) return value

  for (const key of keys) {
    if (value === key) return key
    const { current, english } = labelsFor(key)
    if (value === current || value === english) return key
  }

  const target = normalize(value)
  if (target) {
    for (const key of keys) {
      if (normalize(key) === target) return key
      const { current, english } = labelsFor(key)
      if (normalize(current) === target || normalize(english) === target)
        return key
    }
  }

  return value
}
